import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { FieldConfig, ForgeQuery } from '../../components/ForgeQueryBuilder';
import { mainBodyStoreUnionSingleSelect } from '../../components/Business/QueryFieldPresets';
import { trafficQualityService } from '../../services/statistics/traffic-quality';
import { useMainBodyMap, useShopMap, useAuditors } from '../../services/store/organization.store';
import type { TrafficQualityQuery } from '../../services/statistics/traffic-quality';
import type { TrafficQualityTrendRowEntity, TrafficQualityTrendStatsEntity } from '../../types/entity/traffic-quality';
import TrendComboChart from '../../components/Charts/TrendComboChart';
import Dashboard from '../../components/Templates/Dashboard';
import { getDefaultQuery, kpiFromStats } from './shop.effect';

const TrafficTrend: React.FC = () => {
  const { mainBodyList } = useMainBodyMap();
  const { shopList } = useShopMap();
  const { auditorList } = useAuditors();  

  const fields: FieldConfig[] = [
    { 
      key: 'period', 
      label: '统计周期', 
      group: 'append', 
      required: true,
      type: 'select', 
      width: 75,
      options: [
        { label: '日', value: '日' },
        { label: '周', value: '周' },
        { label: '月', value: '月' },
      ],
      placeholder: '请选择',
      onValueChange: () => ({ orderCreatedAt: [] }) 
    },
    { 
      key: 'orderCreatedAt', 
      group: 'order', 
      label: '接单日期', 
      type: 'date', 
      required: true,
      operators: ['between'], 
      format: 'YYYY-MM-DD', 
      placeholder: ['开始日期', '结束日期'],
      disabledDate: (current: any, values: Record<string, any>, dates?: any[]) => {
        if (!current) return false;
        const period = values['period'] || '月';
        if (dates && dates.length > 0) {
           const date0 = dates[0] || dates[1];
           if (date0) {
             const diff = Math.abs(current.diff(date0, 'day'));
             if (period === '日' && diff > 31) return true;
             if (period === '周' && diff > 366) return true;
           }
        }
        return current > dayjs().endOf('day');
      }
    },
    mainBodyStoreUnionSingleSelect(mainBodyList, shopList, 'order'),
  ];

  const defaultQ = useMemo(() => {
    const end = dayjs().endOf('day');
    const start = end.subtract(11, 'month').startOf('month');
    return {
      filter: {
        order: {
          orderCreatedAt: { operator: 'between', value: [start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')] }
        },
        append: {
          period: { operator: 'eq', value: '月' }
        }
      }
    } as any;
  }, []);

  return (
    <Dashboard<TrafficQualityTrendRowEntity>
      title={'流量质量趋势'}
      queryFields={fields}
      fetcher={async (params: TrafficQualityQuery) => {
        const [rows, stats] = await Promise.all([
          trafficQualityService.getTrafficQualityTrendRows(params as any),
          trafficQualityService.getTrafficQualityTrendStats(params as any),
        ]);
        return { rows, stats };
      }}
      defaultQuery={defaultQ}
      kpiItems={(stats) => kpiFromStats(stats)}
      sections={[
        {
          key: 'order-shipment',
          title: '订单发货数趋势',
          render: ({ rows }) => {
            const seriesCfg = {
              'shippedOrders': { showName: '可发货订单', kind: 'column', color: '#5B8FF9' },
              'riskPassOrders': { showName: '通过订单', kind: 'column', color: '#5AD8A6' },
              'receivedOrders': { showName: '已发货订单', kind: 'line', color: '#FF6F61' },
            } as any;
            return <TrendComboChart title="" data={rows} seriesCfg={seriesCfg} />;
          },
        },
        {
          key: 'traffic-quality',
          title: '流量来源趋势',
          render: ({ rows }) => {
            const seriesCfg = {
              blindShipOrders: { showName: '盲发', kind: 'line', color: '#5AD8A6' },
              riskPassOrders: { showName: '通过', kind: 'line', color: '#5B8FF9' },
              prePaidOrders: { showName: '预收', kind: 'line', color: '#F6BD16' },
              highRiskOrders: { showName: '高风险', kind: 'line', color: '#FF6F61' },
            } as any;
            return <TrendComboChart title="" data={rows} seriesCfg={seriesCfg} />;
          },
        },
        {
          key: 'business-type',
          title: '业务类型分布',
          render: ({ rows }) => {
            const seriesCfg = {
              returnAnytimeOrders: { showName: '随租随还', kind: 'column', color: '#2FC25B' },
              returnAtEndOrders: { showName: '期满可归还', kind: 'column', color: '#3B85F6' },
            } as any;
            return <TrendComboChart title="" data={rows} seriesCfg={seriesCfg} />;
          },
        },
        {
          key: 'device-type',
          title: '设备类型趋势',
          render: ({ rows }) => {
            const seriesCfg = {
              newAppleCount: { showName: '全新苹果', kind: 'line', color: '#7262FD' },
              secondAppleCount: { showName: '二手苹果', kind: 'line', color: '#78D3F8' },
              newAndroidCount: { showName: '全新安卓', kind: 'line', color: '#FF9D4D' },
            } as any;
            return <TrendComboChart title="" data={rows} seriesCfg={seriesCfg} />;
          },
        },
      ]}
    />
  );
};

export default TrafficTrend;
